import type { IMarketplaceSettings } from '../organization/projectSlice';

import { fetchNinjaCsrf, getCachedNinjaCsrf, setCachedNinjaCsrf } from '@/libs/ninja/csrf';
import { BACKEND_SERVER_NINJA } from '@/settings';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'react-toastify';
import { StatusEnum } from '../common-types';
import type { ITokenWithUserId } from '../organization/organizationSlice';
import type { IUidToken } from './interfaces';
axios.defaults.xsrfHeaderName = 'X-CSRFTOKEN';
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.withCredentials = true;

export interface ISettings {
  marketplaces: IMarketplaceSettings[];
}

export interface IUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  date_joined: Date | string;
  activity?: number;
  settings?: ISettings;
  is_staff?: boolean;
  is_superuser?: boolean;
  org_permissions?: {
    slug: string;
    is_owner: boolean;
    is_admin: boolean;
    can_invite: boolean;
  }[];
}

export interface ILoginCredentials {
  username: string;
  password: string;
}

export interface IRegistrationCredentials extends ILoginCredentials {
  email: string;
}

export interface IAuthState {
  user: IUser | null;
  isAuthenticated: boolean;
  status: StatusEnum; // add status to handle loading state
  meStatus: StatusEnum;
  statusText: string | null; // add error to handle any error that may occur during the request
}

export const initialState: IAuthState = {
  user: null,
  isAuthenticated: false,
  status: StatusEnum.idle,
  meStatus: StatusEnum.idle,
  statusText: null,
};

const identifyUser = (user: IUser) => {
  ym(101622263, 'userParams', {
    email: user.email,
    django_id: user.id,
    UserID: user.email as unknown as number,
    ClientID: user.email as unknown as number,
  });
};

// Async thunk for login
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: ILoginCredentials) => {
    const { data } = await axios.post<{ csrf_token?: string }>(
      `${BACKEND_SERVER_NINJA}/auth/login`,
      credentials
    );
    if (data?.csrf_token) setCachedNinjaCsrf(data.csrf_token);
    const { data: user } = await axios.get(`${BACKEND_SERVER_NINJA}/auth/me`);
    identifyUser(user);
    return user;
  }
);

export interface IApiErrorObject {
  code?: string;
  message?: string;
}

export interface IMessageResponse {
  message?: string;
  error?: string | IApiErrorObject | null;
}

export const registration = createAsyncThunk(
  'auth/users',
  async (credentials: IRegistrationCredentials) => {
    try {
      console.log('BACKEND_SERVER_NINJA', BACKEND_SERVER_NINJA);
      const { data } = await axios.post<IMessageResponse>(
        `${BACKEND_SERVER_NINJA}/auth/register`,
        credentials
      );
      if (data?.error) {
        const msg =
          typeof data.error === 'string'
            ? data.error
            : data.error?.message || 'Registration failed';
        toast.error(msg);
        throw new Error(msg);
      }
      return data.message ?? 'OK';
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const api = error.response?.data as
          | IMessageResponse
          | { [key: string]: string[] }
          | undefined;

        // New API shape: { message: string, error: { code, message } }
        if (api && (api as IMessageResponse).error !== undefined) {
          const err = (api as IMessageResponse).error;
          const msg =
            typeof err === 'string'
              ? err
              : err?.message || (api as IMessageResponse).message || 'Registration failed';
          toast.error(msg);
          throw new Error(msg);
        }

        // Legacy field errors: { username: [..], email: [..], password: [..] }
        if (api && !('error' in (api as Record<string, unknown>))) {
          const dict = api as { [key: string]: string[] };
          if (dict?.['username'] || dict?.['email'] || dict?.['password']) {
            let errorText = '';
            for (const errorArray of Object.values(dict)) {
              for (const errorMessage of errorArray) {
                errorText = errorText + errorMessage + ' ';
              }
            }
            toast.error(errorText.trim());
            throw new Error(errorText.trim());
          }
        }
      }
      throw error;
    }
  }
);

// Async thunk for logout
export const logout = createAsyncThunk('auth/logout', async () => {
  const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
  const { data } = await axios.post<{ csrf_token?: string }>(
    `${BACKEND_SERVER_NINJA}/auth/logout`,
    {},
    { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
  );
  if (data?.csrf_token) setCachedNinjaCsrf(data.csrf_token);
});

// Async thunk for check auth
export const me = createAsyncThunk('auth/me', async () => {
  const { data: user } = await axios.get<IUser>(`${BACKEND_SERVER_NINJA}/auth/me`);
  identifyUser(user);
  return user;
});

export const registerFromInvite = createAsyncThunk(
  'organization/register-from-invite',
  async ({
    user_id,
    token,
    password,
  }: { password: string } & ITokenWithUserId) => {
    try {
      const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
      const { data } = await axios.post(
        `${BACKEND_SERVER_NINJA}/invitations/${user_id}/${token}`,
        { password },
        { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
      );
      return data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const data: { [key: string]: string[] } = error.response?.data;
        let errorText = '';
        if (data?.['username'] || data?.['email'] || data?.['password']) {
          for (const errorArray of Object.values(data)) {
            for (const errorMessage of errorArray) {
              errorText = errorText + errorMessage + ' ';
            }
          }
          throw toast.error(errorText);
        }
      }
      throw error;
    }
  }
);

export const activateAccount = createAsyncThunk(
  'auth/activate',
  async ({ uid, token }: IUidToken) => {
    try {
      const { data } = await axios.get<IMessageResponse>(
        `${BACKEND_SERVER_NINJA}/auth/activate/${uid}/${token}`
      );
      if (data.error) {
        const e = data.error;
        const msg = typeof e === 'string' ? e : e?.message;
        throw new Error(msg || 'Activation failed');
      }
      return data.message;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const api = error.response?.data as Partial<IMessageResponse> | undefined;
        const errObj = api?.error;
        const msg =
          (typeof errObj === 'string' ? errObj : errObj?.message) ||
          api?.message ||
          error.message;
        throw new Error(msg || 'Activation failed');
      }
      throw error;
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/reset-password',
  async ({ email }: { email: string }) => {
    try {
      const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
      const { data } = await axios.post<IMessageResponse>(
        `${BACKEND_SERVER_NINJA}/auth/reset-password/request`,
        { email },
        { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
      );
      if (data?.error) {
        const msg =
          typeof data.error === 'string'
            ? data.error
            : data.error?.message || 'Reset password failed';
        toast.error(msg);
        throw new Error(msg);
      }
      return data.message ?? 'OK';
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const api = error.response?.data as Partial<IMessageResponse> | undefined;
        const errObj = api?.error;
        if (errObj !== undefined) {
          const msg =
            (typeof errObj === 'string' ? errObj : errObj?.message) ||
            api?.message ||
            'Reset password failed';
          toast.error(msg);
          throw new Error(msg);
        }
      }
      throw error;
    }
  }
);

export const resetPasswordConfirm = createAsyncThunk(
  'auth/reset-password-confirm',
  async ({ uid, token, password }: IUidToken & { password: string }) => {
    try {
      const csrf = getCachedNinjaCsrf() ?? (await fetchNinjaCsrf());
      await axios.post(
        `${BACKEND_SERVER_NINJA}/auth/reset-password/confirm/${uid}/${token}`,
        { password },
        { withCredentials: true, headers: { 'X-CSRFTOKEN': csrf } }
      );
      return 'ok';
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const api = error.response?.data as
          | IMessageResponse
          | { [key: string]: string[] }
          | undefined;

        // New API shape: { message, error: { code, message } }
        if (api && (api as IMessageResponse).error !== undefined) {
          const errObj = (api as IMessageResponse).error;
          const msg =
            (typeof errObj === 'string' ? errObj : errObj?.message) ||
            (api as IMessageResponse).message ||
            'Password reset failed';
          throw new Error(msg);
        }

        // Legacy field errors: { new_password: [..] }
        if (api && !('error' in (api as Record<string, unknown>))) {
          const dict = api as { [key: string]: string[] };
          if (dict?.['new_password']) {
            let errorText = '';
            for (const errorArray of Object.values(dict)) {
              for (const errorMessage of errorArray) {
                errorText = errorText + errorMessage + ' ';
              }
            }
            throw new Error(errorText.trim());
          }
        }
      }
      throw error;
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthIdle: (state) => {
      state.status = StatusEnum.idle;
      state.meStatus = StatusEnum.idle;
      state.statusText = null;
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(me.pending, (state) => {
        state.meStatus = StatusEnum.loading;
      })
      .addCase(me.fulfilled, (state, action) => {
        state.meStatus = StatusEnum.succeeded;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(me.rejected, (state) => {
        state.meStatus = StatusEnum.failed;
      })
      .addCase(login.pending, (state) => {
        state.status = StatusEnum.loading;
        state.meStatus = StatusEnum.loading;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = StatusEnum.succeeded;
        state.meStatus = StatusEnum.succeeded;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.meStatus = StatusEnum.failed;
        if (action.error.message === 'Request failed with status code 423') {
          state.statusText = 'Активируйте ваш аккаунт через email';
        } else if (action.error.code === 'ERR_BAD_REQUEST') {
          state.statusText = 'Invalid username or password';
        } else {
          state.statusText = action.error.message ?? null;
        }
        toast.error(state.statusText ?? 'Непредвиденная ошибка');
      })
      .addCase(registration.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(registration.fulfilled, (state) => {
        state.status = StatusEnum.succeeded;
        toast.success('Регистрация прошла успешно, подтвердите ваш email.');
      })
      .addCase(registration.rejected, (state) => {
        state.status = StatusEnum.failed;
      })
      .addCase(logout.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.status = StatusEnum.idle;
        state.meStatus = StatusEnum.failed;
        state.statusText = null;
        localStorage.removeItem('activeOgraniationSlug');
      })
      .addCase(logout.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.status = StatusEnum.idle;
        state.meStatus = StatusEnum.failed;
        state.statusText = null;
        localStorage.removeItem('activeOgraniationSlug');
      })
      .addCase(registerFromInvite.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(registerFromInvite.fulfilled, (state) => {
        state.status = StatusEnum.succeeded;
        toast.success('Регистрация прошла успешно, подтвердите ваш email.');
      })
      .addCase(registerFromInvite.rejected, (state) => {
        state.status = StatusEnum.failed;
      })
      .addCase(activateAccount.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(activateAccount.fulfilled, (state) => {
        state.status = StatusEnum.succeeded;
      })
      .addCase(activateAccount.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(resetPassword.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.status = StatusEnum.succeeded;
        toast.success('Ссылка для сброса пароля отправлена на ваш email');
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      })
      .addCase(resetPasswordConfirm.pending, (state) => {
        state.status = StatusEnum.loading;
      })
      .addCase(resetPasswordConfirm.fulfilled, (state) => {
        state.status = StatusEnum.succeeded;
        toast.success('Пароль обновлен');
      })
      .addCase(resetPasswordConfirm.rejected, (state, action) => {
        state.status = StatusEnum.failed;
        state.statusText = action.error.message ?? null;
        toast.error(state.statusText);
      });
  },
});

export const { setAuthIdle } = authSlice.actions;

export default authSlice.reducer;
